/**
 * ScanLog v4 — IndexedDB schema
 *
 * Per spec §3.1. Dexie 4.
 * Master Log (Google Sheet) is source of truth; IDB is a cache/queue.
 *
 * Schema changes require a Dexie version bump + migration.
 */

import Dexie, { type Table } from 'dexie';

export type SyncState = 'synced' | 'pending' | 'conflict';

export interface Item {
	/** EAN/UPC/ISBN — primary key */
	barcode: string;
	product: string;
	brand: string;
	image: string | null;
	source: string;
	sourceUrl: string | null;
	buyLink: string | null;
	/** 15-tab taxonomy; see spec §K */
	tab: string;
	category: string;
	firstScanned: string;
	lastScanned: string;
	quantity: number;
	/**
	 * 'conflict' is a transient marker set in the doGet read path (spec §3.2 P2-1).
	 * Never persists — resolves to 'synced' in the same transaction after the toast.
	 */
	syncState: SyncState;
	/**
	 * v5.2.8 — BEACON-13 Phase 2.3: rich metadata fields, primarily
	 * populated by vision adapters but also usable by barcode-cascade
	 * hits that return structured data. All optional; absent fields
	 * simply don't render in the detail view. Non-breaking additive
	 * change — no Dexie version bump needed because IDB stores typed
	 * objects freely; these properties are just ignored by older builds.
	 */
	/** Package-declared size/count: "25 Chewable Tablets", "10 mL", "Pack of 24". */
	size?: string;
	/** Flavor/scent/color/edition variant: "Berry", "Fresh Linen", "Matte Black". */
	variant?: string;
	/** One-line summary for list subtitle + detail hero. */
	description?: string;
	/** Structured bullet highlights from the packaging (first 3–6 are rendered). */
	attributes?: string[];
	/** Key ingredients/active components where applicable. */
	keyIngredients?: string[];
	/** Vision confidence 0..1 if source is vision:*. */
	visionConfidence?: number;
	/** Model that produced the vision extraction, for audit. */
	visionModel?: string;
	/** Raw JSON/text response from the vision model — hidden behind a debug toggle. */
	visionRawText?: string;
	/**
	 * v5.3.13 CCQ-327 R3 M-2 — set true when the row was hydrated from the
	 * server with `tab=null` (server's column-K ARRAYFORMULA hadn't spilled
	 * yet). The next pull MUST re-fetch the tab for this barcode regardless
	 * of whether `lastScanned` has advanced — because the ARRAYFORMULA
	 * filling in K does NOT bump the row's timestamp, so the standard
	 * `serverTs > local.lastScanned` gate would never re-pull. Cleared
	 * when a pull returns a non-null `tab`.
	 *
	 * Not indexed — we iterate locally on next-pull start to find rows
	 * with `tabPending === true` and add them to the targeted re-fetch list.
	 */
	tabPending?: boolean;
}

/**
 * v4.1.9 CQA-8 CRIT: `synced` is stored as 0 | 1 (NOT boolean).
 *
 * IndexedDB cannot index boolean values per spec — a compound-index lookup
 * like `.where('synced').equals(0)` returned 0 results when rows used
 * `synced: false`, because the `synced` index silently contained zero
 * entries. The earlier "normalize to boolean" upgrade (v4.1.5 H-3/M-2)
 * made this worse, not better. Numeric 0/1 is the ONLY value that lets
 * the index function.
 *
 * Any code setting `synced: false/true` will skip the Sheet silently.
 * Writers MUST use 0 | 1.
 */
export interface Event {
	id?: number;
	barcode: string;
	timestamp: string;
	synced: 0 | 1;
}

export interface MetaKV {
	key: string;
	value: unknown;
}

/**
 * v5.0.0 — Phase 2.0 miss telemetry entry (BEACON-13).
 *
 * Pushed to META_KEYS.MISS_LOG (rolling window, last 50) every time the
 * server cascade returns `{ found: false }`. We capture the trace the
 * server already returns so we can diagnose miss patterns offline and
 * later feed labeled misses into Phase 2.1 (vision-fallback baseline).
 */
export interface MissEntry {
	barcode: string;
	timestamp: string;
	trace?: unknown;
}

/**
 * v5.0.0 — Phase 2.0 camera-capture Blob cache (BEACON-13).
 *
 * One row per capture. Keyed by a monotonically-generated id so multiple
 * photos for the same barcode don't collide, and the id can be linked
 * back to an `events` row when the vision pipeline lights up in v5.1.
 *
 * Blobs stay local until Phase 2.1 — no upload path exists yet.
 */
export interface CapturedImage {
	id?: number;
	barcode: string;
	blob: Blob;
	mime: string;
	capturedAt: string;
	/** Optional link to the matching events row once saved. */
	eventId?: number;
	/**
	 * v5.3.11 CCQ-327 H4 — byte size cached at write time. Lets
	 * `pruneImagesOverCap()` and `estimateImageStorage()` compute totals
	 * by iterating a cursor over the size-only column without
	 * materializing every Blob in memory. Pre-v5.3.11 code called
	 * `.toArray()` on the images table — at the 200MB cap on iOS Safari
	 * this drove heap pressure past the tab-kill threshold.
	 *
	 * Backfilled by the v6 upgrade hook for existing rows; set at
	 * save-time for new rows (see saveCapturedImage).
	 */
	byteSize?: number;
}

/**
 * v4.1.5 C-2 — legacy slug map for the Dexie v2 upgrade.
 *
 * Pre-v4.1.5 builds used a different slug dialect (from an early
 * ad-hoc taxonomy). Server-side TAB_LABEL_TO_SLUG is now the SSOT and uses
 * 15 canonical slugs. On upgrade, every cached item's `tab` field is mapped
 * through this table; unknown slugs fall back to 'other'.
 *
 * Keep this map forever — users upgrading across many versions must still
 * land on a valid slug on first open.
 */
const LEGACY_SLUG_MAP: Record<string, string> = {
	food: 'food-beverage',
	beverage: 'food-beverage',
	pet: 'food-beverage',
	books: 'books-media',
	media: 'books-media',
	household: 'cleaning-household',
	'paper-plastic': 'cleaning-household',
	baby: 'baby-kids',
	tools: 'tools-hardware',
	office: 'office-stationery',
	pharmacy: 'health-meds',
	'home-garden': 'home-goods',
	auto: 'auto-parts',
	sports: 'sports-outdoors',
	garden: 'garden-outdoor'
};

const CANONICAL_SLUGS = new Set([
	'food-beverage',
	'personal-care',
	'health-meds',
	'cleaning-household',
	'books-media',
	'tools-hardware',
	'electronics',
	'home-goods',
	'baby-kids',
	'auto-parts',
	'office-stationery',
	'sports-outdoors',
	'garden-outdoor',
	'apparel',
	'other'
]);

export class ScanLogDB extends Dexie {
	items!: Table<Item, string>;
	events!: Table<Event, number>;
	meta!: Table<MetaKV, string>;
	/**
	 * v5.0.0 — Phase 2.0 (BEACON-13): captured product photos, stored
	 * locally only. Vision pipeline (Phase 2.1) reads from here.
	 */
	images!: Table<CapturedImage, number>;

	constructor() {
		super('scanlog-v4');

		this.version(1).stores({
			items: 'barcode, tab, lastScanned, syncState',
			events: '++id, barcode, timestamp, synced',
			meta: 'key'
		});

		// v4.1.5 — C-2: migrate items.tab from legacy slugs to the canonical
		// 15-tab taxonomy (server-side TAB_DEFS SSOT). Indexes unchanged;
		// Dexie lets us do a no-op schema bump purely to run the upgrade hook.
		this.version(2)
			.stores({
				items: 'barcode, tab, lastScanned, syncState',
				events: '++id, barcode, timestamp, synced',
				meta: 'key'
			})
			.upgrade(async (tx) => {
				await tx
					.table<Item, string>('items')
					.toCollection()
					.modify((it) => {
						const current = String(it.tab || '').trim();
						if (CANONICAL_SLUGS.has(current)) return;
						const mapped = LEGACY_SLUG_MAP[current];
						it.tab = mapped || 'other';
					});
			});

		// v4.1.5 — H-3/M-2: normalize events.synced (first attempt, BROKEN —
		// normalized to pure boolean, which IDB can't index). Kept as a
		// no-schema-change bump so existing users don't trip the v3→v4
		// migration ordering.
		this.version(3)
			.stores({
				items: 'barcode, tab, lastScanned, syncState',
				events: '++id, barcode, timestamp, synced',
				meta: 'key'
			})
			.upgrade(async (tx) => {
				await tx
					.table<Event, number>('events')
					.toCollection()
					.modify((ev) => {
						// HISTORICAL BUG: v3 migration normalized to boolean, which
						// IDB silently rejects as an index value. Preserved here
						// so users who already ran v3 still pass through, and v4
						// below re-normalizes to the correct 0|1. Cast through
						// unknown because the current type is 0|1 and we're
						// intentionally writing a boolean to reproduce history.
						const raw = ev.synced as unknown;
						(ev as unknown as { synced: boolean }).synced = raw === true || raw === 1;
					});
			});

		// v4.1.9 — CQA-8 CRIT fix: re-normalize events.synced to 0|1. The v3
		// upgrade stored booleans, which IndexedDB silently refuses to index
		// (spec: only number | string | Date | ArrayBuffer | Array-of-those
		// are valid keys). Result: `db.events.where('synced').equals(0)`
		// returned 0 rows for every user, sync driver thought nothing was
		// pending, scans never left the phone. RF's production loss.
		//
		// Writers (scan/+page.svelte, scan/manual/+page.svelte, protocol.ts)
		// are ALSO updated to write numeric 0|1 going forward.
		this.version(4)
			.stores({
				items: 'barcode, tab, lastScanned, syncState',
				events: '++id, barcode, timestamp, synced',
				meta: 'key'
			})
			.upgrade(async (tx) => {
				await tx
					.table<Event, number>('events')
					.toCollection()
					.modify((ev) => {
						const raw = ev.synced as unknown;
						ev.synced = raw === true || raw === 1 ? 1 : 0;
					});
			});

		// v5.0.0 — Phase 2.0 (BEACON-13): introduce `images` store for camera
		// captures and seed miss-telemetry meta keys. This is additive — no
		// existing rows are touched. Users upgrading from 4.x jump straight
		// to Dexie v5 on first open; the `images` table is created empty and
		// the two META keys initialize on first miss. Major version bump
		// advertises the narrative shift (cascade-only → cascade + manual +
		// vision), even though the schema delta is conservative.
		this.version(5)
			.stores({
				items: 'barcode, tab, lastScanned, syncState',
				events: '++id, barcode, timestamp, synced',
				meta: 'key',
				images: '++id, barcode, capturedAt, eventId'
			});

		// v6 — v5.3.11 CCQ-327 H4: add `byteSize` to `CapturedImage` so that
		// estimate/prune never materialize the full blob set in JS memory.
		// Schema index unchanged — byteSize is a regular data field, not
		// queried directly. Upgrade backfills existing rows from blob.size.
		this.version(6)
			.stores({
				items: 'barcode, tab, lastScanned, syncState',
				events: '++id, barcode, timestamp, synced',
				meta: 'key',
				images: '++id, barcode, capturedAt, eventId'
			})
			.upgrade(async (tx) => {
				await tx
					.table('images')
					.toCollection()
					.modify((row: CapturedImage) => {
						if (row.byteSize == null && row.blob instanceof Blob) {
							row.byteSize = row.blob.size;
						}
					});
			});
	}
}

export const db = new ScanLogDB();

/** Meta keys — typed accessors. */
export const META_KEYS = {
	/** ISO-8601 ms UTC with Z suffix (spec §3.1 P3-1). */
	LAST_SYNC_CURSOR: 'lastSyncCursor',
	PENDING_COUNT: 'pendingCount',
	RESOLUTION_RETRIES: 'resolutionRetries',
	/** Boolean, default true (spec §2.5b P2-2). */
	PREFS_RAPID_SCAN: 'prefs.rapidScan',
	/**
	 * v5.0.0 Phase 2.0 (BEACON-13) — lifetime count of cascade misses
	 * (`{ found: false }` responses from the server). Incremented on the
	 * client at scan time, NOT derived from the Sheet. Never reset
	 * automatically; user can clear from Settings.
	 */
	MISS_COUNT: 'missCount',
	/**
	 * v5.0.0 Phase 2.0 (BEACON-13) — rolling window of the last 50 miss
	 * entries (`MissEntry[]`). Exported as JSON from Settings and used in
	 * Phase 2.1 to pick the first vision-fallback targets.
	 */
	MISS_LOG: 'missLog',
	/**
	 * v5.1.2 Phase 2.3 (BEACON-13) — user preference for which vision
	 * provider to use by default on /scan/photo/. Values: 'anthropic' |
	 * 'openai' | 'gemini'. Default 'anthropic'. Server adapter falls back
	 * to 'anthropic' if unknown value is sent.
	 */
	PREFS_VISION_PROVIDER: 'prefs.visionProvider',
	/**
	 * v5.2.9 Phase 2.3 — opt-in multi-provider comparison mode.
	 * When true, the photo scan path calls `visionCompare` instead of
	 * `vision` and presents a side-by-side chooser. Default false.
	 */
	PREFS_VISION_COMPARE: 'prefs.visionCompare',
	/**
	 * v5.2.9 Phase 2.3 — rolling window of the last N compare-mode
	 * results with per-field disagreement summary. Used to drive the
	 * Phase 3 decision (grounding vs. single-provider vs. extended
	 * thinking).
	 */
	VISION_COMPARE_LOG: 'visionCompareLog',
	/**
	 * v5.3.3 BDES-47 P1-UX-1 — opt-in camera-capture debug diagnostics.
	 * When true, /scan/photo's CameraCapture renders the raw `track:live
	 * res:… decode:… blob=… <img> onload=…` telemetry below the
	 * preview image. Default false; production users never see it. RF
	 * flips this on for the BEACON-13 H-2 black-frame investigation.
	 */
	PREFS_DEBUG_CAMERA_DIAG: 'prefs.debugCameraDiag'
} as const;

export const VISION_COMPARE_LOG_MAX = 50;

/** Rolling window cap for MISS_LOG. */
export const MISS_LOG_MAX = 50;

export async function getMeta<T = unknown>(key: string, fallback: T): Promise<T> {
	const row = await db.meta.get(key);
	return (row?.value as T) ?? fallback;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
	await db.meta.put({ key, value });
}

/**
 * v5.0.3 XR M-5 (CCQ-309): prune orphan image blobs to keep `db.images`
 * bounded and avoid IDB quota eviction.
 *
 * JPEG 0.85 captures run ~1–4 MB each. With no pruning, a user on rapid-
 * scan who takes photos on most misses can accumulate tens to hundreds of
 * MB over a few weeks. Once the browser evicts our origin's IDB for quota
 * pressure, the ENTIRE scanlog DB is dropped — items, events, and the
 * sync cursor included. That's catastrophic.
 *
 * Policy:
 *   • Delete orphan rows (no `eventId`) older than ORPHAN_TTL_MS (48h).
 *     48h is long enough to cover a normal save-later workflow and short
 *     enough to bound quota growth.
 *   • Linked rows (with `eventId`) are preserved for the v5.1 vision
 *     pipeline; they will be pruned in a later phase once their event has
 *     synced and the vision API has consumed them.
 *
 * Call from app init (fire-and-forget). Failure is logged and swallowed
 * — pruning is an optimization, not a correctness requirement.
 */
export const IMAGE_ORPHAN_TTL_MS = 48 * 60 * 60 * 1000; // 48 hours

/**
 * v5.0.9 CCQ-309 R2 M-1 (BEACON-13): retention policy for LINKED images
 * (rows with `eventId`). Once the owning event has synced to the sheet,
 * the local blob is a backup-only copy — the v5.1 vision pipeline
 * consumer is expected to run post-sync. Keep for 7 days as a safety
 * window for local-only consumers, then prune.
 *
 * Policy chosen over reviewer's (a) LRU-byte-cap to keep the prune
 * deterministic (same time-based mental model as orphan prune) and
 * avoid `blob.size` scans on every app start. The LRU-byte-cap ALSO
 * exists as a last-resort backstop via pruneImagesOverCap().
 */
export const IMAGE_LINKED_SYNCED_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * v5.0.9 CCQ-309 R2 M-1 (BEACON-13): hard byte cap — last-resort
 * backstop if the TTL pruner hasn't caught up (e.g., user imports a
 * backlog or the vision pipeline lands later than expected). When
 * `sum(blob.size) > IMAGE_TOTAL_BYTES_CAP`, oldest-first prune of
 * synced-linked rows until we're back under. Orphans (pruned by TTL)
 * and not-yet-synced-linked rows are NEVER touched here.
 */
export const IMAGE_TOTAL_BYTES_CAP = 200 * 1024 * 1024; // 200 MB

/**
 * v5.0.9 CQA-8 F-L-1 (BEACON-13): accept an optional db handle so this
 * function is directly unit-testable against a per-test fake-indexeddb DB
 * (same pattern as `recordMiss` from M-4). Production call site in
 * `+layout.svelte` still uses the singleton via the default param.
 */
export async function pruneOrphanImages(
	now: number = Date.now(),
	dbHandle: ScanLogDB = db
): Promise<{ deleted: number }> {
	const cutoffIso = new Date(now - IMAGE_ORPHAN_TTL_MS).toISOString();
	try {
		const deleted = await dbHandle.transaction('rw', dbHandle.images, async () => {
			// v5.0.4 XR R2 M-2 (CCQ-309): use the existing `capturedAt` index
			// to narrow before filtering, and use `primaryKeys()` so we only
			// hold IDs in memory — not the rows. Prior implementation used
			// `.filter(...).toArray()` which bypassed the index entirely AND
			// materialized full Blob objects in memory: hundreds of MB of churn
			// on cold start once `db.images` fills up. The cursor still has to
			// read each (capturedAt-narrowed) record value to evaluate the
			// `eventId` JS filter — a compound `(eventId, capturedAt)` index
			// would let us skip that, but it requires a schema bump and the
			// narrowed scan is already orders of magnitude smaller than the
			// full table for any well-aged DB.
			const candidateIds = await dbHandle.images
				.where('capturedAt')
				.below(cutoffIso)
				.filter((row) => row.eventId === undefined)
				.primaryKeys();
			if (candidateIds.length === 0) return 0;
			await dbHandle.images.bulkDelete(candidateIds);
			return candidateIds.length;
		});
		return { deleted };
	} catch (err) {
		console.warn('[scanlog] pruneOrphanImages failed:', err);
		return { deleted: 0 };
	}
}

/**
 * v5.0.9 CCQ-309 R2 M-1 (BEACON-13): TTL-based prune of LINKED image rows
 * whose owning event has already synced to the sheet.
 *
 * Fire-and-forget from app init alongside `pruneOrphanImages`. Safe to
 * re-run any number of times; if the v5.1 vision pipeline hasn't
 * consumed the blob yet, it has 7 days of local-only window before
 * deletion.
 *
 * Rule: delete `images` row where `row.eventId` points to an `events`
 * row that is `synced === 1`, AND the image `capturedAt` is older than
 * `IMAGE_LINKED_SYNCED_TTL_MS`.
 *
 * Not atomic with writes on `events.synced` — if an image is mid-sync
 * when the prune runs, worst case we keep it one cycle longer. That's
 * the safe side of the race.
 */
export async function pruneSyncedLinkedImages(
	now: number = Date.now(),
	dbHandle: ScanLogDB = db
): Promise<{ deleted: number }> {
	const cutoffIso = new Date(now - IMAGE_LINKED_SYNCED_TTL_MS).toISOString();
	try {
		const deleted = await dbHandle.transaction(
			'rw',
			dbHandle.images,
			dbHandle.events,
			async () => {
				// Narrow by capturedAt index; only rows older than cutoff.
				const stale = await dbHandle.images
					.where('capturedAt')
					.below(cutoffIso)
					.filter((row) => row.eventId !== undefined)
					.toArray();
				if (stale.length === 0) return 0;

				// Fetch their owning events in one pass; keep only those that have synced.
				const eventIds = stale.map((r) => r.eventId!).filter((id): id is number => id != null);
				const events = await dbHandle.events.where('id').anyOf(eventIds).toArray();
				const syncedEventIds = new Set(
					events.filter((e) => e.synced === 1).map((e) => e.id!)
				);
				const toDelete = stale
					.filter((row) => syncedEventIds.has(row.eventId!))
					.map((row) => row.id!);
				if (toDelete.length === 0) return 0;
				await dbHandle.images.bulkDelete(toDelete);
				return toDelete.length;
			}
		);
		return { deleted };
	} catch (err) {
		console.warn('[scanlog] pruneSyncedLinkedImages failed:', err);
		return { deleted: 0 };
	}
}

/**
 * v5.0.9 CCQ-309 R2 M-1 (BEACON-13): last-resort byte-cap backstop.
 * Prunes oldest synced-linked rows until total image bytes are back
 * under `IMAGE_TOTAL_BYTES_CAP`.
 *
 * This is the safety net for the case where:
 *   - TTL-based prune hasn't run recently (user opens app after long gap)
 *   - User has imported a big backlog of events+photos
 *   - The v5.1 vision pipeline isn't consuming fast enough
 *
 * Fire-and-forget from Settings and from app init (post-TTL-prune).
 */
export async function pruneImagesOverCap(
	dbHandle: ScanLogDB = db
): Promise<{ deleted: number; bytesBefore: number; bytesAfter: number }> {
	try {
		return await dbHandle.transaction(
			'rw',
			dbHandle.images,
			dbHandle.events,
			async () => {
				// v5.3.11 CCQ-327 H4 — cursor-based iteration. Collect metadata
				// ONLY (id, eventId, byteSize) instead of materializing the
				// full rows (which includes the Blob payload). At the 200MB
				// cap on iOS Safari, `.toArray()` blew the heap.
				//
				// byteSize is backfilled by the v6 migration; fall through to
				// row.blob.size only if byteSize is missing (shouldn't happen
				// post-migration but costs nothing to guard).
				interface PruneCandidate {
					id: number;
					eventId?: number;
					byteSize: number;
				}
				const candidates: PruneCandidate[] = [];
				let totalBytes = 0;
				await dbHandle.images
					.orderBy('capturedAt')
					.each((row) => {
						const sz = row.byteSize ?? row.blob?.size ?? 0;
						candidates.push({ id: row.id!, eventId: row.eventId, byteSize: sz });
						totalBytes += sz;
					});

				if (totalBytes <= IMAGE_TOTAL_BYTES_CAP) {
					return { deleted: 0, bytesBefore: totalBytes, bytesAfter: totalBytes };
				}

				// Only prune synced-linked rows. Orphans and not-yet-synced
				// linked rows stay — same safety contract as TTL prune.
				const eventIds = candidates
					.map((r) => r.eventId)
					.filter((id): id is number => id != null);
				const events = await dbHandle.events.where('id').anyOf(eventIds).toArray();
				const syncedEventIds = new Set(
					events.filter((e) => e.synced === 1).map((e) => e.id!)
				);

				let bytes = totalBytes;
				const toDelete: number[] = [];
				for (const row of candidates) {
					if (bytes <= IMAGE_TOTAL_BYTES_CAP) break;
					if (row.eventId != null && syncedEventIds.has(row.eventId)) {
						toDelete.push(row.id);
						bytes -= row.byteSize;
					}
				}
				if (toDelete.length === 0) {
					return { deleted: 0, bytesBefore: totalBytes, bytesAfter: totalBytes };
				}
				await dbHandle.images.bulkDelete(toDelete);
				return { deleted: toDelete.length, bytesBefore: totalBytes, bytesAfter: bytes };
			}
		);
	} catch (err) {
		console.warn('[scanlog] pruneImagesOverCap failed:', err);
		return { deleted: 0, bytesBefore: 0, bytesAfter: 0 };
	}
}

/**
 * v5.0.9 CCQ-309 R2 M-1 (BEACON-13): compute image storage stats for
 * Settings “Barcode photos” section. Combined with
 * `navigator.storage.estimate()`, gives the user visibility into quota
 * pressure before the browser starts auto-evicting.
 */
export async function estimateImageStorage(
	dbHandle: ScanLogDB = db
): Promise<{
	imageCount: number;
	linkedCount: number;
	orphanCount: number;
	totalBytes: number;
	quotaBytes: number | null;
	usageBytes: number | null;
}> {
	// v5.3.11 CCQ-327 H4 — cursor-based; never materialize Blobs.
	let imageCount = 0;
	let linkedCount = 0;
	let totalBytes = 0;
	await dbHandle.images.toCollection().each((row) => {
		imageCount++;
		if (row.eventId != null) linkedCount++;
		totalBytes += row.byteSize ?? row.blob?.size ?? 0;
	});
	const orphanCount = imageCount - linkedCount;

	let quotaBytes: number | null = null;
	let usageBytes: number | null = null;
	if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
		try {
			const e = await navigator.storage.estimate();
			quotaBytes = e.quota ?? null;
			usageBytes = e.usage ?? null;
		} catch {
			/* ignore — quota is advisory, not correctness */
		}
	}

	return {
		imageCount,
		linkedCount,
		orphanCount,
		totalBytes,
		quotaBytes,
		usageBytes
	};
}
